import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DOCUMENT,
  inject,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { TranslocoPipe } from '@jsverse/transloco';
import { Router } from '@angular/router';
import { translation } from '@cue/translations';
import { ButtonComponent } from '@cue/ui';
import { cueRefresh, IconComponent } from '@cue/ui/icons';
import { timer } from 'rxjs';
import { map } from 'rxjs/operators';
import { rxResource, toSignal } from '@angular/core/rxjs-interop';
import { NavigationService } from '@cue/navigation-services';
import { explicitEffect } from 'ngxtension/explicit-effect';
import { NavigationConfigService } from '../config.service';
import { injectQueryParams } from 'ngxtension/inject-query-params';

@Component({
  selector: 'server-not-alive',
  templateUrl: './server-not-alive.component.html',
  styleUrls: ['./server-not-alive.component.scss'],
  imports: [TranslocoPipe, ButtonComponent, IconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ServerNotAliveComponent implements OnInit, OnDestroy {
  protected readonly translation = translation;
  protected ICONS = {
    cueRefresh,
  };

  private doc = inject(DOCUMENT);
  private configService = inject(NavigationConfigService);
  private router = inject(Router);
  private navigationService = inject(NavigationService);
  private intervalId: number;
  protected refreshIntervalInSec = 30;
  protected countDown = signal<number>(30);

  checkServerOnlineTrigger = timer(0, this.refreshIntervalInSec * 1000).pipe(
    map((x) => {
      return x;
    })
  );
  checkServerOnlineSignal = toSignal(this.checkServerOnlineTrigger);
  checkManuallyTriggered = signal(false);

  returnUrl = injectQueryParams('returnUrl');

  serverAlive = rxResource({
    params: () => {
      return {
        automaticTrigger: this.checkServerOnlineSignal(),
        manualTrigger: this.checkManuallyTriggered(),
      };
    },
    stream: ({ params }) => this.navigationService.serverAvailable(),
  });

  serverAliveData = computed(() => {
    if (this.serverAlive.hasValue()) {
      return this.serverAlive.value();
    }
    return null;
  });

  checkServerAvailability = explicitEffect([this.serverAliveData], ([serverAliveData]) => {
    if (serverAliveData && serverAliveData.alive) {
      const returnUrl = this.returnUrl() || '/';
      // check origin
      const safe = returnUrl && returnUrl.startsWith('/') && !returnUrl.startsWith('//') && !returnUrl.includes('http');
      if (safe) {
        void this.router.navigateByUrl(returnUrl);
      } else {
        void this.router.navigate(['/']);
      }
    }
  });

  ngOnInit(): void {
    const el = this.doc.getElementById('appLoading');
    if (el) {
      el.parentElement.removeChild(el);
    }

    if (this.configService.value.enabled) {
      this.router.navigate(['']);
    }

    this.intervalId = setInterval((x) => {
      let updatedValue = this.countDown() - 1;
      if (updatedValue < 0) {
        updatedValue = this.refreshIntervalInSec;
      }
      this.countDown.set(updatedValue);
    }, 1000);
  }

  ngOnDestroy(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  protected forceManualTrigger() {
    this.checkManuallyTriggered.set(true);
    this.checkManuallyTriggered.set(false);
  }
}
